import { EditStudentToolbarTab } from '@constants';
import { IUser } from '@sdk/contracts';
import { Button, Drawer, Form, Modal, Tabs } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StudentExtraDataForm } from '../StudentExtraDataForm';
import { StudentGeneralInfoForm } from '../StudentGeneralInfoForm';
import { StudentTrainingInfoForm } from '../StudentTrainingInfoForm';

import { Wrapper } from './EditStudentDrawer.styles';
import { useEditStudentDrawerFormLogic } from './hooks';

import { Box } from '@/components/Utils';
import { useEditStudentDrawer } from '@/hooks';
import { useProfile } from '@/store';

type ITabs = keyof typeof EditStudentToolbarTab;

const DRAWER_WIDTH = 750;

export const EditStudentDrawer = () => {
  const [modal, contextHolder] = Modal.useModal();
  const { props, isOpen, data, setIsOpen, setData } = useEditStudentDrawer();
  const [form] = Form.useForm<IUser>();
  const { t } = useTranslation();
  const {
    roleCheck: { isManager }
  } = useProfile();
  const [activeTab, setActiveTab] = useState<ITabs>(EditStudentToolbarTab.GENERAL_INFO);

  const {
    studentTrainingInfoRef,
    handleFinish,
    closeDrawerDirectly,
    isWorking,
    userInfo,
    onboarding,
    onboardingChanges,
    handleOnboardingChange
  } = useEditStudentDrawerFormLogic({
    form,
    data,
    drawerProps: props,
    setData,
    setIsOpen
  });

  const handleCancel = () => {
    modal.confirm({
      title: t('DISCARD_CHANGES_QUESTION'),
      content: t('DISCARD_CHANGES_DESCRIPTION'),
      okText: t('CLOSE_AND_DISCARD'),
      cancelText: t('KEEP_EDITING'),
      onOk: closeDrawerDirectly
    });
  };

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ ...data, userInfo });
    }
  }, [data, userInfo]);

  return (
    <>
      {contextHolder}
      <Drawer
        open={isOpen}
        forceRender
        width={DRAWER_WIDTH}
        onClose={handleCancel}
        title={data ? t('EDIT_STUDENT') : t('NEW_STUDENT')}
        footer={
          <Box justifyContent="space-between">
            <Button loading={isWorking} onClick={handleCancel}>
              {t('CANCEL')}
            </Button>
            <Button loading={isWorking} type="primary" onClick={form.submit}>
              {data ? t('SAVE_CHANGES') : t('CREATE_STUDENT')}
            </Button>
          </Box>
        }
        {...props}>
        <Form
          layout="vertical"
          onFinish={handleFinish}
          form={form}
          initialValues={{ ...data, userInfo }}>
          <Wrapper $showtabs={isManager}>
            <Tabs
              activeKey={activeTab}
              onChange={key => setActiveTab(key as ITabs)}
              items={[
                {
                  label: t('GENERAL_INFORMATION'),
                  key: EditStudentToolbarTab.GENERAL_INFO,
                  children: <StudentGeneralInfoForm form={form} />
                },
                {
                  label: t('SPECIFIC_INFORMATION'),
                  key: EditStudentToolbarTab.SPECIFIC_INFO,
                  children: (
                    <StudentTrainingInfoForm ref={studentTrainingInfoRef} data={data} form={form} />
                  )
                },
                {
                  label: t('EXTRA_DATA'),
                  key: EditStudentToolbarTab.EXTRA_DATA,
                  children: (
                    <StudentExtraDataForm
                      form={form}
                      onboarding={{ ...onboarding, ...onboardingChanges }}
                      onOnboardingChange={handleOnboardingChange}
                    />
                  )
                }
              ]}
            />
          </Wrapper>
        </Form>
      </Drawer>
    </>
  );
};
